<script setup>
import Card from "@/components/Tools/Card/Card.vue";
import Header from "@/components/ExternalApps/SalesChannelManagementApp/components/Header.vue";
import Progressbar from "@/components/Tools/Progressbar.vue";
import Modal from "@/components/Tools/Modal.vue";
import { useApp } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useApp";
import { useAppStatus } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useAppStatus";
import { useIntegration } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useIntegration";
import { useIntegrationStatus } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useIntegrationStatus";
import { useStore } from "@/core/services/store";
import { useMigrationIntegration } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useMigrationIntegration";
import { nextTick, onMounted, onUnmounted, watch } from "vue";
import {
  addEventToLoadingQueue,
  removeEventFromLoadingQueue
} from "@/composables/useLoadingQueue";
import { LOAD_CONFIG_VALUES } from "@/core/services/store/variables_v1.module";
import {
  loadProjects,
  setAvailableFeatures
} from "@/composables/useSalesChannelManagementApp";
import { useRouter } from "vue-router/composables";
import router from "@/router";
import { useShopifyOauth } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useShopifyOauth";
import Forms from "@/components/ExternalApps/classes/Forms";
import i18n from "@/core/plugins/vue-i18n";
import { useAmazonOauth } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useAmazonOauth";
import { useEbayOauth } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useEbayOauth";
import { useBexioOauth } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useBexioOauth";
import { useEtsyOauth } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useEtsyOauth";
import useOauth from "@/components/ExternalApps/SalesChannelManagementApp/composables/useOauth";
import { useNavigationEvent } from "@/components/ExternalApps/composables/useNavigationEvent";
import Swal from "sweetalert2";
import _ from "lodash";

const store = useStore();
const { applyShopifyOAuthValues } = useShopifyOauth();
const { applyAmazonOAuthValues } = useAmazonOauth();
const { applyEbayOAuthValues } = useEbayOauth();
const { applyBexioOAuthValues } = useBexioOauth();
const { applyEtsyOAuthValues } = useEtsyOauth();
const { integrationsWithPresets, integrations, getIntegrationsWithPresets } =
  useIntegration();
const {
  isBusyMount,
  isBusy,
  initialized,
  selectIntegration,
  setXentralCustomVariables
} = useApp();
const {
  isProductsSelected,
  isMigrationFinished,
  hasMigration,
  hasJournal,
  hasOnlyMigration
} = useAppStatus();

const {
  hasUnmigratedChannels,
  show,
  migrationButtonText,
  migrationButtonIcon,
  createFirstIntegrationDescriptionText
} = useMigrationIntegration();

const { navigate } = useNavigationEvent();

const { base64ToUtf8 } = useOauth();

const {
  statusDialog,
  selectedIntegrationForStatus,
  getStatusSeverity,
  getStatusColor,
  getStatusMessage,
  getStatusActionMessage,
  shouldShowWarningIcon,
  openStatusDialog,
  closeStatusDialog,
  handleReauthorize,
  onOpenError,
  getChannelInfo
} = useIntegrationStatus();

// Update integration when a new one is added
watch(
  () => integrations.value,
  async () => await getIntegrationsWithPresets()
);

watch(
  () => isBusy.value,
  function (val) {
    if (val || initialized.value) {
      return;
    }

    initialized.value = true;
  }
);

onMounted(async () => {
  if (store.getters?.isDev && !store.getters?.selectedApp) {
    const router = useRouter();
    await router.push({ name: "salesChannelManagementApps" });

    return;
  }

  // Prevent unnecessary requests
  const { toggleStatus, isAppInitialized } = useAppStatus();
  const { loadChannelsForShop } = useMigrationIntegration();

  if (isAppInitialized && integrations.value.length > 0) return;
  addEventToLoadingQueue({ key: "mountApp", group: "mountApp" });

  await store.dispatch("variables/" + LOAD_CONFIG_VALUES);
  await loadProjects(true);
  const wizardForm = await Forms.getScmaForm(store.getters.selectedApp.name, {
    fallbackFormName:
      store.getters.appType === store.getters.selectedApp.name
        ? "Default"
        : store.getters.appType,
    sections: ["wizard"]
  });
  const features = wizardForm.data?.wizard?.features?.map(f => f.name) || [];
  setAvailableFeatures(features);

  // Wait for the next tick to ensure that the availableFeatures are set before loading the integrations
  await nextTick();

  await Promise.all([getIntegrationsWithPresets(), loadChannelsForShop()]);

  toggleStatus(false);
  setXentralCustomVariables();

  removeEventFromLoadingQueue({ key: "mountApp", group: "mountApp" });

  let oAuthState = store.getters.xentralConfig?.data?.["state"];

  if (!oAuthState || !oAuthState?.length) return;

  try {
    oAuthState = base64ToUtf8(oAuthState);
  } catch (error) {
    //
  }

  // After successful oauth authorization, go back into installation progress
  if (oAuthState?.migration || oAuthState?.fetched) {
    if (store.getters.selectedApp.name === "amazon") {
      await applyAmazonOAuthValues();
    } else if (store.getters.selectedApp.name === "shopify") {
      await applyShopifyOAuthValues();
    } else if (store.getters.selectedApp.name === "ebay") {
      await applyEbayOAuthValues();
    } else if (store.getters.selectedApp.name === "bexio") {
      await applyBexioOAuthValues();
    } else if (store.getters.selectedApp.name === "etsy") {
      await applyEtsyOAuthValues();
    }

    return;
  }

  if (oAuthState?.error) {
    let errorMessageTitle =
      "salesChannelManagementApp.otto.errorHandling.ottoInstallationNotFoundTitle";
    let errorMessage =
      "salesChannelManagementApp.otto.errorHandling.ottoInstallationNotFound";

    await Swal.fire({
      icon: "error",
      title: i18n.t(errorMessageTitle),
      html: i18n.t(errorMessage, {
        link: process.env.VUE_APP_OTTO_APP_INSTALLATION_URL
      }),
      showConfirmButton: false,
      width: "30%"
    });

    return;
  }

  await router.push({ name: "salesChannelManagementAppWizard" });
});

// Add and remove click event listener on elements with class 'softmigration__link'
onMounted(() => {
  nextTick(() => {
    const links = document.querySelectorAll(".softmigration__link");
    const handler = e => {
      e.preventDefault();
      const link = "/app/settings";
      const params = {
        menu: "order-management-saleschannels",
        submenu: "online-shops"
      };

      navigate(link, params, "_blank");
    };
    links.forEach(el => el.addEventListener("click", handler));
    // Remove event listeners on unmount
    onUnmounted(() => {
      links.forEach(el => el.removeEventListener("click", handler));
    });
  });
});
</script>

<template>
  <div>
    <Progressbar />
    <div class="pt-3">
      <b-skeleton-wrapper v-if="isBusyMount">
        <b-skeleton height="50px" width="100%" class="mb-4"></b-skeleton>
        <b-skeleton
          v-for="n in 4"
          :key="'skeleton-integration-' + n"
          height="155px"
          width="100%"
          class="mb-4"
        ></b-skeleton>
      </b-skeleton-wrapper>
      <div v-else>
        <!--    START: HEADER    -->
        <Header />
        <!--    END: HEADER    -->

        <div
          v-if="
            $store.getters.selectedApp?.name === 'amazon' &&
            hasUnmigratedChannels
          "
          class="alert alert-info bg-white mb-4"
        >
          <div
            class="ml-2"
            v-html="
              i18n.t(
                'salesChannelManagementApp.migration.amazonMigrationNotice'
              )
            "
          ></div>
        </div>

        <!--    START: CARD-LISTING    -->
        <div v-if="integrationsWithPresets.length > 0">
          <Card
            v-for="integration in integrationsWithPresets"
            :key="integration.name"
            class="mb-4 p-4 integration"
            :class="{
              'integration--error':
                integration.salesChannel?.value?.migrationError?.message
                  ?.length > 0 ||
                integration.salesChannel?.value?.migrationError?.length > 0,
              'integration--critical':
                getStatusSeverity(integration) === 'critical',
              'integration--warning':
                getStatusSeverity(integration) === 'warning',
              [getStatusColor(getStatusSeverity(integration))
                .containerClass]: true
            }"
          >
            <div>
              <div class="mb-2 d-flex justify-content-between">
                <div class="d-flex align-center">
                  <span class="font-weight-bold text-h5">
                    {{
                      integration?.label ??
                      integration?.name?.replace(
                        store.getters.selectedApp.name,
                        ""
                      )
                    }}
                  </span>

                  <div
                    class="status ml-2"
                    :class="{ 'ml-1': shouldShowWarningIcon(integration) }"
                  >
                    <span v-if="integration.softMigrated" class="gray">
                      {{
                        $t(
                          "salesChannelManagementApp.migration.migrationPending"
                        )
                      }}
                    </span>
                    <span v-else-if="integration.productiveMode" class="green">
                      {{ $t("salesChannelManagementApp.migration.productive") }}
                    </span>
                    <span v-else class="gray">
                      {{
                        $t("salesChannelManagementApp.migration.development")
                      }}</span
                    >
                  </div>
                </div>

                <button
                  v-if="integration.softMigrated"
                  id="softmigration-notice"
                  class="mr-auto pt-1"
                >
                  <i class="fal fa-circle-info"></i>
                </button>

                <b-popover target="softmigration-notice" triggers="hover">
                  <span
                    v-html="
                      i18n.t(
                        'salesChannelManagementApp.migration.migrationPendingHelp',
                        { app: _.upperFirst(store.getters.selectedApp?.name) }
                      )
                    "
                  ></span>
                </b-popover>

                <button
                  v-if="!integration.softMigrated"
                  v-b-popover.hover.top="
                    $t('salesChannelManagementApp.titles.settings.title')
                  "
                  class="btn btn-icon text-h6"
                  @click="
                    selectIntegration(
                      integration,
                      'settings',
                      'settings',
                      'wizard'
                    )
                  "
                >
                  <i class="fal fa-gear"></i>
                </button>
              </div>
              <!--    START: ACTIONS    -->
              <div
                v-if="!integration.softMigrated"
                class="row justify-content-between"
              >
                <div class="col-9">
                  <div
                    v-if="
                      Object.keys(integration.featureStatus || {}).length > 0
                    "
                    :class="{
                      'mb-4':
                        !isMigrationFinished(integration) ||
                        !isProductsSelected(integration) ||
                        integration.salesChannel?.value?.isMigrated
                    }"
                  >
                    <span class="mb-1">
                      {{
                        $t(
                          "salesChannelManagementApp.migration.activeFeatures"
                        )
                      }}:
                    </span>
                    <span
                      v-for="(value, feature) in integration.featureStatus"
                      :key="feature"
                      class="status mb-1"
                    >
                      <span :class="[value ? 'green' : 'gray']">{{
                        $t("salesChannelManagementApp.titles." + feature)
                      }}</span>
                    </span>
                  </div>

                  <!-- Status Warning Icon -->
                  <div v-if="shouldShowWarningIcon(integration)">
                    <div class="alert alert-info bg-white mb-2">
                      <i class="fal fa-warning"></i>
                      <span class="ml-2"
                        ><b>{{ getStatusMessage(integration) }}:</b>
                        {{ getStatusActionMessage(integration) }}</span
                      >
                      <button
                        class="btn btn-outline-primary ml-2 status-warning-btn pa-2 font-weight-bold"
                        style="color: white"
                        :class="{
                          'btn-warning':
                            getStatusSeverity(integration) === 'warning',
                          'btn-danger':
                            getStatusSeverity(integration) === 'critical'
                        }"
                        :title="getStatusMessage(integration)"
                        @click="openStatusDialog(integration)"
                      >
                        {{
                          i18n.t(
                            "salesChannelManagementApp.integrationStatus.takeAction"
                          )
                        }}
                        <i
                          class="fas"
                          :class="{
                            'fa-exclamation-triangle':
                              getStatusSeverity(integration) === 'warning',
                            'fa-exclamation-circle':
                              getStatusSeverity(integration) === 'critical'
                          }"
                        ></i>
                      </button>
                    </div>
                  </div>

                  <template
                    v-else-if="
                      !integration.salesChannel?.value?.migrationError?.message
                        ?.length &&
                      !integration.salesChannel?.value?.migrationError?.length
                    "
                  >
                    <div
                      v-if="!isMigrationFinished(integration)"
                      class="alert alert-info bg-white mb-0"
                    >
                      <i class="fal fa-warning"></i>
                      <span class="ml-2">{{
                        $t(
                          "salesChannelManagementApp.migration.migrationMissing"
                        )
                      }}</span>
                    </div>
                    <div
                      v-else-if="
                        !isProductsSelected(integration) &&
                        !integration.softMigrated
                      "
                      class="alert alert-info bg-white mb-0"
                    >
                      <i class="fal fa-warning"></i>
                      <span class="ml-2">{{
                        $t(
                          "salesChannelManagementApp.productSelection.productSelectionMissing"
                        )
                      }}</span>
                    </div>
                    <div
                      v-if="
                        integration.salesChannel?.value?.isMigrated &&
                        !integration.wasAlreadyActivated
                      "
                      class="alert alert-info bg-white mb-0"
                    >
                      <i class="fal fa-warning"></i>
                      <div class="ml-2">
                        <span>
                          {{
                            $t(
                              "salesChannelManagementApp.migration.migratedFromSalesChannel",
                              {
                                channelName:
                                  getChannelInfo(integration)?.bezeichnung ||
                                  getChannelInfo(integration)?.name
                              }
                            )
                          }}
                        </span>
                      </div>
                    </div>
                  </template>
                  <div v-else class="alert alert-danger bg-white mb-0">
                    <i class="fal fa-warning"></i>
                    <span class="ml-2">{{
                      integration.salesChannel.value.migrationError?.message ??
                      (typeof integration.salesChannel.value.migrationError ===
                      "string"
                        ? integration.salesChannel.value.migrationError
                        : "")
                    }}</span>
                  </div>
                </div>
                <div class="col-auto d-flex flex-column">
                  <template
                    v-if="
                      !integration.salesChannel?.value?.migrationError?.message
                        ?.length &&
                      !integration.salesChannel?.value?.migrationError?.length
                    "
                  >
                    <!-- START: Initial data import -->
                    <button
                      v-if="
                        store.getters.appConfig?.hasOnlyMigration ||
                        (hasMigration && integration.migrationActive)
                      "
                      :disabled="
                        isMigrationFinished(integration) && !hasOnlyMigration
                      "
                      class="btn btn-outline-primary mb-2"
                      @click="
                        selectIntegration(
                          integration,
                          'migration',
                          'migration',
                          'settings'
                        )
                      "
                    >
                      <span class="px-1">{{
                        $t("salesChannelManagementApp.titles.migration.title")
                      }}</span>
                    </button>
                    <!-- END: Initial data import -->
                    <template v-else>
                      <!-- START: Product selection -->
                      <button
                        v-if="
                          !store.getters?.areaBlacklist?.includes(
                            'productSelection'
                          ) ?? true
                        "
                        :disabled="!isMigrationFinished(integration)"
                        class="btn btn-outline-primary mb-2"
                        @click="
                          selectIntegration(
                            integration,
                            'productSelection',
                            'productSelection',
                            'selection'
                          )
                        "
                      >
                        <span class="px-1">{{
                          $t(
                            "salesChannelManagementApp.titles.productSelection.selection"
                          )
                        }}</span>
                      </button>
                      <!-- END: Product selection -->

                      <!-- START: Workflows -->
                      <button
                        v-if="
                          !store.getters?.areaBlacklist?.includes(
                            'workflows'
                          ) ?? true
                        "
                        :disabled="
                          !isMigrationFinished(integration) ||
                          !isProductsSelected(integration)
                        "
                        class="btn btn-outline-primary mb-2"
                        @click="selectIntegration(integration, 'workflows')"
                      >
                        <span class="px-1">{{
                          $t("salesChannelManagementApp.workflows")
                        }}</span>
                      </button>
                      <!-- END: Workflows -->

                      <!-- START: Journal -->
                      <button
                        v-if="hasJournal"
                        class="btn btn-outline-primary"
                        :disabled="!isProductsSelected(integration)"
                        @click="
                          $router.push({
                            name: 'salesChannelManagementAppJournal'
                          });
                          selectIntegration(
                            integration,
                            'journal',
                            'journal',
                            '',
                            true
                          );
                        "
                      >
                        <span class="px-1">{{
                          $t("salesChannelManagementApp.journal.journal")
                        }}</span>
                      </button>
                      <!-- END: Journal -->
                    </template>
                  </template>
                  <button
                    v-else
                    class="btn btn-outline-primary mb-2"
                    @click="onOpenError(integration)"
                  >
                    <span class="px-1">{{
                      $t("salesChannelManagementApp.migration.moreInformation")
                    }}</span>
                  </button>
                </div>
              </div>
              <!--    END: ACTIONS    -->
            </div>
          </Card>
        </div>
        <div v-else>
          <div class="d-flex justify-content-center w-100">
            <Card class="pa-6">
              <div>
                <h3>
                  {{
                    $t(
                      "salesChannelManagementApp.migration.createFirstIntegrationTitle"
                    )
                  }}
                </h3>
              </div>
              <div class="mb-4">
                {{ createFirstIntegrationDescriptionText }}
              </div>
              <div
                class="d-flex align-items-end"
                :class="
                  store.getters.selectedApp?.name === 'amazon'
                    ? 'justify-content-center'
                    : 'justify-content-between'
                "
              >
                <div
                  v-if="store.getters.selectedApp?.name !== 'amazon'"
                  class="info-card-container"
                >
                  <div class="info-card d-flex p-4">
                    <div class="mr-2">
                      <i class="fal fa-circle-info" />
                    </div>
                    <div v-html="$t('salesChannelManagementApp.help')"></div>
                  </div>
                </div>
                <div>
                  <button
                    v-if="hasUnmigratedChannels"
                    class="btn btn-primary mr-4"
                    @click="show = true"
                  >
                    <i class="fal" :class="migrationButtonIcon" />
                    {{ migrationButtonText }}
                  </button>
                  <button
                    v-if="store.getters.selectedApp?.name !== 'ebay'"
                    class="btn btn-primary"
                    @click="
                      $router.push({ name: 'salesChannelManagementAppWizard' })
                    "
                  >
                    <i class="fal fa-plus" />
                    {{ $t("salesChannelManagementApp.addIntegration") }}
                  </button>
                </div>
              </div>
            </Card>
          </div>
        </div>
        <!--    END: CARD-LISTING    -->
      </div>
    </div>

    <!-- Status Details Modal -->
    <Modal
      :show-modal="statusDialog"
      :title="
        $t('salesChannelManagementApp.integrationStatus.actionRequiredTitle')
      "
      type="small"
      @toggle-modal="closeStatusDialog"
    >
      <template #content>
        <div v-if="selectedIntegrationForStatus" class="mb-3">
          <div
            v-if="getStatusActionMessage(selectedIntegrationForStatus)"
            class="action-message"
          >
            <div
              class="mb-0 mt-1"
              v-html="selectedIntegrationForStatus?.status_comment"
            ></div>
          </div>
        </div>
      </template>

      <template #footer>
        <div class="d-flex justify-content-end">
          <button
            v-if="
              getStatusSeverity(selectedIntegrationForStatus) === 'critical' &&
              selectedIntegrationForStatus.status === 'ACTION_REQUIRED'
            "
            class="btn btn-primary ml-2"
            @click="handleReauthorize(selectedIntegrationForStatus)"
          >
            {{ $t("salesChannelManagementApp.integrationStatus.reauthorize") }}
          </button>
          <button
            v-else-if="
              getStatusSeverity(selectedIntegrationForStatus) === 'warning' &&
              selectedIntegrationForStatus.status === 'EXPIRES_SOON'
            "
            class="btn btn-primary ml-2"
            @click="handleReauthorize(selectedIntegrationForStatus)"
          >
            {{ $t("salesChannelManagementApp.integrationStatus.reauthorize") }}
          </button>
          <button
            v-else-if="
              getStatusSeverity(selectedIntegrationForStatus) !== 'normal'
            "
            class="btn btn-primary ml-2"
            @click="handleReauthorize(selectedIntegrationForStatus)"
          >
            {{ $t("salesChannelManagementApp.integrationStatus.viewSettings") }}
          </button>
        </div>
      </template>
    </Modal>
  </div>
</template>

<style scoped lang="scss">
i {
  color: var(--ax-fg-primary, #{$color-connect-text});
  transition: color 0.15s;
}

.btn-icon {
  &:hover {
    i {
      color: var(--ax-fg-brand-primary, #{$color-connect-primary});
    }
  }
}

.status {
  span {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 3px;
    margin-right: 6px;

    &.green {
      background-color: var(--ax-bg-success-tertiary, #d3f8df) !important;
      color: var(--ax-fg-success-primary, #084c2e) !important;
    }

    &.gray {
      color: var(--ax-fg-secondary, #162a41);
      background-color: var(--ax-bg-tertiary, #eaecf5);
    }

    &.primary {
      color: var(--ax-fg-brand-primary, #{$color-connect-primary});
      background-color: var(
        --ax-bg-brand-tertiary,
        #{$color-connect-primary-light}
      ) !important;
    }
  }
}

.info-card-container {
  .info-card {
    background-color: var(--ax-bg-tertiary, #eaecf0);
    border-radius: 6px;
  }
}

.integration {
  &--error {
    border: 1px solid $color-connect-error;
  }

  &--critical {
    border: 1px solid #fca5a5 !important;
    background-color: #fef2f2 !important;
  }

  &--warning {
    border: 1px solid #fde68a !important;
    background-color: #fffbeb !important;
  }
}

// Integration status utility classes
.integration-status {
  &--critical {
    border-color: #fca5a5 !important;
    background-color: #fef2f2 !important;

    &-text {
      color: #dc2626 !important;
    }

    &-border {
      border-color: #fca5a5 !important;
    }

    &-bg {
      background-color: #fef2f2 !important;
    }
  }

  &--warning {
    border-color: #fde68a !important;
    background-color: #fffbeb !important;

    &-text {
      color: #d97706 !important;
    }

    &-border {
      border-color: #fde68a !important;
    }

    &-bg {
      background-color: #fffbeb !important;
    }
  }
}

// Text-only status classes for use in modals and other components
.status-text {
  &--critical {
    color: #dc2626 !important;
  }

  &--warning {
    color: #d97706 !important;
  }

  &--normal {
    color: inherit !important;
  }
}

.cursor-pointer {
  cursor: pointer;
}

.status-warning-btn {
  padding: 2px 6px;
  font-size: 12px;
  line-height: 1;
  border-radius: 6px;

  &.btn-warning {
    background-color: orange;
    border-color: #fde68a;
    color: #856404;

    &:hover {
      background-color: #fff3cd;
      border-color: #fde68a;
    }
  }

  &.btn-danger {
    background-color: red;
    border-color: #fca5a5;
    color: #721c24;

    &:hover {
      background-color: #f8d7da;
      border-color: #fca5a5;
    }
  }
}

.status-message {
  font-size: 14px;
}

.action-message {
  font-size: 14px;
  color: #666;
}
</style>
